// includes/js-src/janus-engine/pl/php-round.ts — PHP round() / (int) cast twins for the PL port (Janus C2).
// Plan: plans/janus-c2-pl-in-browser-plan-2026-09-16.md §6.5 + §14 amendment 16.
// round(): the corpus already ports PHP 8.5 ext/standard/math.c for places 0 | 2 | 4 (compile/php-decimal-values.ts
// roundPhpDecimal). The pricer also rounds to 1 and 3 places (notes, final_multiplier), so this is the same algorithm
// with the factor generalised. Proven against tools/test/fixtures/pl-cost-selftest.json (PHP-computed grid).
// Algorithm reference: https://github.com/php/php-src/blob/PHP-8.5/ext/standard/math.c  (_php_math_round / php_round_helper)

const FACTORS = [1, 10, 100, 1000, 10000, 100000, 1000000];

/** PHP round($value, $places) for a double: pre-rounding check + half away from zero. */
export function phpRound(value: number, places = 0): number {
    if (!Number.isFinite(value) || value === 0) return value;
    const factor = FACTORS[places] ?? Math.pow(10, places);
    const direction = value < 0 ? -1 : 1;
    let whole = value < 0 ? Math.ceil(value * factor) : Math.floor(value * factor);
    const adjacent = whole + direction;
    if (adjacent / factor === value) whole = adjacent;
    if (Math.abs(whole) >= 1e16) return value;
    if (Math.abs(value) >= Math.abs((whole + direction * 0.5) / factor)) whole += direction;
    const out = whole / factor;
    return out === 0 ? 0 : out; // PHP prints -0.0 as "-0"; the wire never carries it — normalise
}

/** PHP (int) cast of a double: truncation toward zero; NaN/Inf → 0; out-of-range wraps modulo 2^64 on the installed PHP
 * (the selftest pins it) — every priced quantity is far inside the range, so the wrap is parity, not a feature. */
export function phpInt(value: number): number {
    if (!Number.isFinite(value)) return 0;
    const t = Math.trunc(value);
    if (Math.abs(t) < 9007199254740992) return t === 0 ? 0 : t;
    return Number(BigInt.asIntN(64, BigInt(t)));
}

/** The pricer's idiom `(int) round($x)` and `(int) round($x / 100)`. */
export const phpIntRound = (value: number): number => phpInt(phpRound(value, 0));
